import { emptyLoanPaymentDraft, LoanPaymentMethodFields, type LoanPaymentDraft } from "@/components/hr/LoanPaymentMethodFields";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/components/ui/sonner";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { formatAmountCommas } from "@/lib/formatPkrAmount";
import { getEmployeeById, listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import { validateLoanPayment } from "@/lib/hr/loansStore";
import {
  createAdvanceSalaryBatch,
  fetchAdvanceSalaryFormMeta,
  periodKey,
  periodLabel,
  salaryAdvancesApiErrorMessage,
  type AdvanceSalaryFormMeta,
} from "@/lib/hr/salaryAdvancesStore";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import { cn } from "@/lib/utils";
import { CalendarRange, Check, CheckCircle2, ChevronsLeft, ChevronsRight, ChevronsUpDown, HandCoins, Info, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

const MONTHS = [
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
] as const;

function formatMoney(value: string): string {
  const raw = value.replace(/[^\d.]/g, "");
  if (!raw) return value;
  const [whole, ...rest] = raw.split(".");
  const formatted = formatAmountCommas(whole.replace(/\D/g, ""));
  return rest.length ? `${formatted}.${rest.join("")}` : formatted;
}

function safeNumber(v: string): number | null {
  const n = Number(v.replace(/,/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function commaFormat(n: number | null | undefined): string {
  if (n == null) return "0";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

function employeeDisplayName(e: EmployeeRecord): string {
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
}

function employeeLabel(e: EmployeeRecord): string {
  const n = employeeDisplayName(e);
  return e.employeeCode ? `${e.employeeCode} · ${n}` : n;
}

function employeeInitials(e: EmployeeRecord): string {
  const first = (e.firstName || "").trim();
  const last = (e.lastName || "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const full = (e.fullName || "").trim();
  if (full) {
    const parts = full.split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : full.slice(0, 2).toUpperCase();
  }
  return "??";
}

function employeeSubtitle(e: EmployeeRecord): string {
  const parts: string[] = [];
  if (e.departmentName?.trim()) parts.push(e.departmentName.trim());
  if (e.designationName?.trim()) parts.push(e.designationName.trim());
  return parts.join(" · ") || "";
}

function isActiveEmployee(e: EmployeeRecord): boolean {
  return (e.employeeStatus || "").toLowerCase() === "active";
}

function buildPeriodOptions(meta: AdvanceSalaryFormMeta | null): Array<{
  year: number;
  month: number;
  key: string;
  label: string;
}> {
  const blocked = new Set((meta?.blockedPeriods ?? []).map((p) => periodKey(p.year, p.month)));
  const reserved = new Set((meta?.reservedPeriods ?? []).map((p) => periodKey(p.year, p.month)));
  const now = new Date();
  const options: Array<{ year: number; month: number; key: string; label: string }> = [];
  const start = new Date(now.getFullYear(), 0, 1);
  for (let offset = 0; offset < 24; offset += 1) {
    const d = new Date(start.getFullYear(), start.getMonth() + offset, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const key = periodKey(year, month);
    if (blocked.has(key) || reserved.has(key)) continue;
    options.push({ year, month, key, label: periodLabel(year, month, MONTHS) });
  }
  return options;
}

function SalaryPeriodPicker({
  open,
  onOpenChange,
  periodYear,
  periodMonth,
  periodOptions,
  pickerYear,
  onPickerYearChange,
  onSelect,
  disabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodYear: number;
  periodMonth: number;
  periodOptions: Array<{ year: number; month: number; key: string; label: string }>;
  pickerYear: number;
  onPickerYearChange: (year: number) => void;
  onSelect: (year: number, month: number) => void;
  disabled?: boolean;
}) {
  const availableKeys = new Set(periodOptions.map((opt) => opt.key));
  const minPickerYear = periodOptions[0]?.year ?? new Date().getFullYear();
  const maxPickerYear = periodOptions[periodOptions.length - 1]?.year ?? minPickerYear;
  const selectedLabel = periodYear && periodMonth ? periodLabel(periodYear, periodMonth, MONTHS) : null;

  return (
    <Popover modal open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn("glass-input h-10 w-full justify-start text-left font-normal", !selectedLabel && "text-muted-foreground")}
          disabled={disabled}
        >
          <CalendarRange className="mr-2 h-4 w-4 shrink-0 opacity-70" />
          {selectedLabel ?? "Select period"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[100] w-[min(100vw-2rem,20rem)] p-4" align="start" sideOffset={6}>
        <div className="mb-3 space-y-1">
          <p className="text-sm font-semibold text-foreground">Select salary period</p>
          <p className="text-xs text-muted-foreground">This advance will be recovered in the selected payroll month.</p>
        </div>
        {periodOptions.length === 0 ? (
          <p className="rounded-lg border border-border/50 bg-muted/10 p-3 text-center text-xs text-muted-foreground">
            No available salary periods for this employee.
          </p>
        ) : (
          <div className="rounded-lg border border-border/50 bg-muted/10 p-3">
            <div className="mb-3 flex items-center justify-between gap-2 border-b border-border/50 pb-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={pickerYear <= minPickerYear}
                onClick={() => onPickerYearChange(Math.max(minPickerYear, pickerYear - 1))}
                aria-label="Previous year"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold tabular-nums text-foreground">{pickerYear}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={pickerYear >= maxPickerYear}
                onClick={() => onPickerYearChange(Math.min(maxPickerYear, pickerYear + 1))}
                aria-label="Next year"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {MONTH_LABELS.map((label, monthIndex) => {
                const month = monthIndex + 1;
                const key = periodKey(pickerYear, month);
                const isAvailable = availableKeys.has(key);
                const isSelected = periodYear === pickerYear && periodMonth === month;
                return (
                  <button
                    key={label}
                    type="button"
                    disabled={!isAvailable}
                    onClick={() => onSelect(pickerYear, month)}
                    className={cn(
                      "h-10 rounded-md text-sm font-medium transition-colors",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : isAvailable
                          ? "border border-border/50 bg-background text-foreground hover:bg-muted"
                          : "cursor-not-allowed border border-transparent bg-muted/30 text-muted-foreground/50",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export type SalaryAdvanceCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, employee is locked (employee profile). Omit on /payroll/salary-advances. */
  employeeId?: string;
  onSaved?: () => void;
};

export function SalaryAdvanceCreateDialog({ open, onOpenChange, employeeId, onSaved }: SalaryAdvanceCreateDialogProps) {
  const lockedEmployeeId = employeeId || "";
  const allowEmployeeSelect = !lockedEmployeeId;

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(lockedEmployeeId);
  const [employee, setEmployee] = useState<EmployeeRecord | undefined>(() =>
    lockedEmployeeId ? getEmployeeById(lockedEmployeeId) : undefined,
  );
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [periodYear, setPeriodYear] = useState(0);
  const [periodMonth, setPeriodMonth] = useState(0);
  const [reason, setReason] = useState("");
  const [paymentDraft, setPaymentDraft] = useState<LoanPaymentDraft>(emptyLoanPaymentDraft());
  const [formMeta, setFormMeta] = useState<AdvanceSalaryFormMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [empTick, setEmpTick] = useState(0);
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerCycle, setEmployeePickerCycle] = useState(0);
  const [periodPickerOpen, setPeriodPickerOpen] = useState(false);
  const [periodPickerYear, setPeriodPickerYear] = useState(() => new Date().getFullYear());

  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);
  const amountNum = safeNumber(advanceAmount);
  const periodOptions = useMemo(() => buildPeriodOptions(formMeta), [formMeta]);
  const remaining = formMeta && amountNum ? Math.max(0, formMeta.grossSalary - amountNum) : formMeta?.grossSalary ?? null;

  const employeeName = employee
    ? employee.fullName || `${employee.firstName} ${employee.lastName}`.trim() || "Employee"
    : "Employee";
  const employeeInitial = employeeName.trim().charAt(0).toUpperCase() || "E";

  useEffect(() => {
    if (!open) return;
    setAdvanceAmount("");
    setPeriodYear(0);
    setPeriodMonth(0);
    setReason("");
    setPaymentDraft(emptyLoanPaymentDraft());
    setFormMeta(null);
    setPeriodPickerOpen(false);
    setEmployeePickerOpen(false);
    setSelectedEmployeeId(lockedEmployeeId);
    setEmployee(lockedEmployeeId ? getEmployeeById(lockedEmployeeId) : undefined);
  }, [open, lockedEmployeeId]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    void Promise.all([fetchBanks(), syncEmployeesFromApi()])
      .then(([rows]) => {
        if (mounted) {
          setBanks(rows);
          setEmpTick((t) => t + 1);
        }
      })
      .catch(() => {
        if (mounted) setBanks([]);
      });
    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !selectedEmployeeId) {
      setFormMeta(null);
      return;
    }
    let cancelled = false;
    setMetaLoading(true);
    void fetchAdvanceSalaryFormMeta(selectedEmployeeId)
      .then((meta) => {
        if (!cancelled) setFormMeta(meta);
      })
      .catch((e) => {
        if (!cancelled) {
          setFormMeta(null);
          toast.error(salaryAdvancesApiErrorMessage(e));
        }
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedEmployeeId]);

  const close = () => onOpenChange(false);

  const save = async () => {
    if (saving) return;
    if (!selectedEmployeeId) return toast.error("Select an employee.");
    if (!formMeta) return toast.error("Employee salary details are still loading.");
    if (!amountNum || amountNum <= 0) return toast.error("Advance amount must be greater than zero.");
    if (!periodYear || !periodMonth) return toast.error("Select a salary period.");
    if (amountNum > formMeta.grossSalary) {
      return toast.error(`Advance amount cannot exceed gross salary (${formatAmountCommas(String(formMeta.grossSalary))}).`);
    }
    const paymentError = validateLoanPayment(paymentDraft);
    if (paymentError) return toast.error(paymentError);

    setSaving(true);
    try {
      const result = await createAdvanceSalaryBatch({
        employeeId: selectedEmployeeId,
        lines: [
          {
            advanceAmount: amountNum,
            periodYear,
            periodMonth,
            reason: reason.trim() || null,
          },
        ],
        paymentMethod: paymentDraft.paymentMethod,
        bankId: paymentDraft.bankId,
        referenceNo: paymentDraft.referenceNo,
        chequeNo: paymentDraft.chequeNo,
        chequeDate: paymentDraft.chequeDate,
      });
      toast.success(result.message);
      close();
      onSaved?.();
    } catch (e) {
      toast.error(salaryAdvancesApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
              <HandCoins className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">Record Salary Advance</DialogTitle>
              <p className="text-xs text-muted-foreground">Issue one advance against an upcoming payroll month.</p>
            </div>
          </div>
        </DialogHeader>

        {allowEmployeeSelect ? (
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-foreground">
              Employee <span className="text-destructive">*</span>
            </Label>
            <Popover
              modal
              open={employeePickerOpen}
              onOpenChange={(next) => {
                setEmployeePickerOpen(next);
                if (next) setEmployeePickerCycle((c) => c + 1);
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={employeePickerOpen}
                  className={cn("glass-input h-10 w-full justify-between px-3 font-normal", !employee && "text-muted-foreground")}
                >
                  {employee ? (
                    <span className="flex items-center gap-2 truncate">
                      <Avatar className="h-5 w-5 text-[8px]">
                        {employee.photoDataUrl ? (
                          <AvatarImage src={employee.photoDataUrl} alt={employeeDisplayName(employee)} />
                        ) : null}
                        <AvatarFallback className="text-[8px] bg-primary/10 text-primary">{employeeInitials(employee)}</AvatarFallback>
                      </Avatar>
                      <span className="truncate">{employeeLabel(employee)}</span>
                    </span>
                  ) : (
                    <span className="truncate text-left">Select employee…</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                <Command key={employeePickerCycle} shouldFilter>
                  <CommandInput placeholder="Search name, code or department…" className="h-9" />
                  <CommandList>
                    <CommandEmpty>No employee found.</CommandEmpty>
                    <CommandGroup>
                      {activeEmployees.map((e) => {
                        const label = employeeLabel(e);
                        const subtitle = employeeSubtitle(e);
                        const selected = selectedEmployeeId === e.id;
                        const searchValue = [label, e.employeeCode, e.departmentName, e.designationName, e.id]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <CommandItem
                            key={e.id}
                            value={searchValue}
                            onSelect={() => {
                              setSelectedEmployeeId(e.id);
                              setEmployee(e);
                              setAdvanceAmount("");
                              setPeriodYear(0);
                              setPeriodMonth(0);
                              setReason("");
                              setEmployeePickerOpen(false);
                            }}
                            className="gap-2"
                          >
                            <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                            <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                              {e.photoDataUrl ? <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} /> : null}
                              <AvatarFallback className="text-[10px] bg-primary/10 text-primary">{employeeInitials(e)}</AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col min-w-0">
                              <span className="truncate text-sm font-medium">{label}</span>
                              {subtitle ? <span className="truncate text-[11px] opacity-60 leading-tight">{subtitle}</span> : null}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        ) : null}

        {employee ? (
          <div className="flex items-center justify-between bg-muted/30 border border-border/50 rounded-xl p-3 text-xs">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center font-bold text-primary text-xs overflow-hidden shrink-0">
                {employee.photoDataUrl ? <img src={employee.photoDataUrl} alt="" className="h-full w-full object-cover" /> : employeeInitial}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-foreground truncate">{employeeName}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {employee.designationName || "Staff"} · {employee.employeeCode || "—"}
                </p>
              </div>
            </div>
            <div className="text-right shrink-0 pl-3">
              <p className="text-[10px] uppercase font-semibold text-muted-foreground">Gross monthly salary</p>
              <p className="font-bold text-primary">
                {metaLoading ? "…" : formatCurrency(formMeta?.grossSalary ?? 0)}
              </p>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground flex justify-between items-center">
                <span>
                  Advance Amount <span className="text-destructive">*</span>
                </span>
                {amountNum ? <span className="text-xs text-emerald-600 font-bold">Rs. {commaFormat(amountNum)}</span> : null}
              </Label>
              <div className="relative">
                <Input
                  className="glass-input h-10 pr-12 font-semibold"
                  value={advanceAmount}
                  onChange={(e) => setAdvanceAmount(formatMoney(e.target.value))}
                  placeholder="e.g. 20,000"
                  inputMode="decimal"
                  disabled={!selectedEmployeeId}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  PKR
                </span>
              </div>
              {formMeta?.grossSalary ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <span className="text-[10px] text-muted-foreground self-center mr-1">Quick:</span>
                  {[0.25, 0.5, 0.75, 1].map((ratio) => {
                    const calcAmt = Math.round(formMeta.grossSalary * ratio);
                    return (
                      <button
                        key={ratio}
                        type="button"
                        onClick={() => setAdvanceAmount(formatAmountCommas(String(calcAmt)))}
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
                      >
                        {Math.round(ratio * 100)}%
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground">
                Salary Period <span className="text-destructive">*</span>
              </Label>
              <SalaryPeriodPicker
                open={periodPickerOpen}
                onOpenChange={(next) => {
                  setPeriodPickerOpen(next);
                  if (next) setPeriodPickerYear(periodYear || periodOptions[0]?.year || new Date().getFullYear());
                }}
                periodYear={periodYear}
                periodMonth={periodMonth}
                periodOptions={periodOptions}
                pickerYear={periodPickerYear}
                onPickerYearChange={setPeriodPickerYear}
                onSelect={(year, month) => {
                  setPeriodYear(year);
                  setPeriodMonth(month);
                  setPeriodPickerOpen(false);
                }}
                disabled={!selectedEmployeeId || metaLoading || !formMeta}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground">Reason / Note</Label>
              <Input
                className="glass-input h-10"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Emergency advance request"
                disabled={!selectedEmployeeId}
              />
            </div>
          </div>

          <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
            <div>
              <div className="flex items-center gap-2 pb-3 border-b border-primary/10">
                <HandCoins className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Advance Preview</h4>
              </div>
              <div className="space-y-3 pt-3 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Gross salary cap</span>
                  <span className="font-bold text-foreground text-sm">
                    {formMeta ? formatCurrency(formMeta.grossSalary) : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Advance given</span>
                  <span className="font-bold text-amber-700">{amountNum ? formatCurrency(amountNum) : "—"}</span>
                </div>
                <div className="flex justify-between items-center border-t border-primary/10 pt-2">
                  <span className="text-muted-foreground">Recovered in</span>
                  <span className="font-extrabold text-primary text-sm">
                    {periodYear && periodMonth ? periodLabel(periodYear, periodMonth, MONTHS) : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Remaining of cap</span>
                  <span className="font-bold text-foreground">{remaining != null && formMeta ? formatCurrency(remaining) : "—"}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-primary/10">
              <Info className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>Recovered automatically when that month’s salary is finalized.</span>
            </div>
          </div>
        </div>

        <LoanPaymentMethodFields
          value={paymentDraft}
          banks={banks}
          onChange={(patch) => setPaymentDraft((d) => ({ ...d, ...patch }))}
        />

        <DialogFooter className="pt-4 border-t border-border/50 gap-2">
          <Button variant="outline" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || !selectedEmployeeId || metaLoading}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Recording...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Record Advance
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
